import React, { useState, useEffect } from 'react';
import * as propz from 'propz';
import {
  getTournamentResultsFormattedByAgeGroups,
  assignPositions,
} from 'helpers/score';
import { SchoolEvent } from 'models/event';
import { Tournament } from 'models/tournament';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled, experimental_sx as sx } from '@mui/system';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const TableWrapper = styled(Box)(
  sx({
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '6px',
    overflow: 'hidden',
    width: '100%',
    backgroundColor: 'transparent',
  })
);

const TableHeadCell = styled(TableCell)(
  sx({
    fontWeight: 'bold',
    fontSize: '1.8rem',        
    bgcolor: '#fafafa',
    color: '#222',
    py: 1.5,                   
    whiteSpace: 'nowrap',
  })
);

interface Props {
  events: SchoolEvent[];
  tournament: Tournament;
  pageIndex: number;
  pageCount: number;
}

export default function TVOverallByAgeGroup({
  events,
  tournament,
  pageIndex,  
  pageCount
}: Props) {

  const data = getTournamentResultsFormattedByAgeGroups(events, tournament);
  const rows: any[] = propz.get(data, ['scores', 'Overall results'], []);
  if (rows.length === 0) return null;

  const pageSize = 7;
  const totalPages = pageCount;
  const safePageIndex = pageIndex >= 0 && pageIndex < totalPages ? pageIndex : 0;

  const sorted = [...rows].sort((a, b) => {
    const aT = Number(a.Total) || 0;
    const bT = Number(b.Total) || 0;
    return bT - aT;
  });

  const ranked = assignPositions(sorted);

  const startIdx = safePageIndex * pageSize;
  const pageRows = ranked.slice(startIdx, startIdx + pageSize);

  const allKeys = Object.keys(ranked[0] || {});
  const ageGroupKeys = allKeys.filter((k) => k !== 'position' && k !== 'Total');

  const mixedKeys = ageGroupKeys.filter((col) => /[/-]/.test(col));
  const pureAgeKeys = ageGroupKeys.filter((col) => !mixedKeys.includes(col));

  return (
    <>
      <TableWrapper>
        <Table
          size="small"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: '0 8px',       
            backgroundColor: 'transparent',
          }}
        >
          <TableHead>
            <TableRow>
              <TableHeadCell 
                align="center" 
                sx={{ 
                  width: '60px' 
                }}>
                #
              </TableHeadCell>
              {pureAgeKeys.map((col) => (
                <TableHeadCell
                  key={col}
                  align={col === 'Team' ? 'left' : 'center'}
                >
                  {col}
                </TableHeadCell>
             ))}
              <TableHeadCell align="center">Mixed ages</TableHeadCell>
              <TableHeadCell align="center">Total</TableHeadCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageRows.map((row: any, idx: number) => {
              let mixedSum = 0;
              for (const mk of mixedKeys) {
                mixedSum += Number(row[mk] || 0);
              }

              const totalVal = Number(row.Total) || 0;
              const isTopThree = row.position <= 3 && totalVal > 0;

              let rowBg = idx % 2 === 0 ? '#ffffff' : '#f9f9f9';
              if (isTopThree) {
                if (row.position === 1) rowBg = 'rgba(255, 243, 224, 1)';   
                if (row.position === 2) rowBg = 'rgba(237, 238, 242, 1)';   
                if (row.position === 3) rowBg = 'rgba(243, 236, 223, 1)';   
              }

              return (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.85)',  
                    borderRadius: '4px',                        
                    '& td': { borderBottom: 'none' },           
                  }}
                >
                <TableCell
                  align="center"
                  sx={{
                    fontSize: '1.6rem',
                    color: '#111',
                    px: 2,
                    py: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                    {row.position}
                    {isTopThree && row.position === 1 && (
                      <EmojiEventsIcon
                        sx={{ ml: 1, color: 'gold', fontSize: '1.6rem' }}
                      />
                    )}
                    {isTopThree && row.position === 2 && (
                      <EmojiEventsIcon
                        sx={{ ml: 1, color: 'silver', fontSize: '1.6rem' }}
                      />
                    )}
                    {isTopThree && row.position === 3 && (
                      <EmojiEventsIcon
                        sx={{ ml: 1, color: '#cd7f32', fontSize: '1.6rem' }}
                      />
                    )}
                  </TableCell>

                  {pureAgeKeys.map((colKey) => (
                    <TableCell
                      key={colKey}
                      align={colKey === 'Team' ? 'left' : 'center'}
                      sx={{
                        fontSize: '1.6rem',
                        color: '#111',
                        px: 0,
                        py: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row[colKey]}
                    </TableCell>
                  ))}

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '1.6rem',
                      color: '#111',
                      px: 0,
                      py: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mixedSum}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: '1.6rem',
                      color: '#111',
                      px: 0,
                      py: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.Total}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>

      {pageCount > 1 && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{ 
              color: '#fff', 
             fontSize: '1.4rem', 
              fontWeight: 'bold' 
            }}
          >
            Page {safePageIndex + 1} of {totalPages}
          </Typography>
        </Box>
      )}
    </>
  );
}
